import { useCallback, useState } from 'react';
import { Button_v2 as Button, Spinner_v2 as Spinner } from '@mindly/ui-components';
import { StripePaymentElement, StripePaymentElementOptions } from '@stripe/stripe-js';
import { IonIcon } from '@ionic/react';
import { checkmark } from 'ionicons/icons';
import { PaymentElement } from '@stripe/react-stripe-js';
import { useLog } from 'src/shared/hooks';
import useToast from 'src/context/ToastContext/ToastContext';
import { useTranslation } from 'react-i18next';

type PaymentElementProps = {
  onReady: (event: StripePaymentElement) => void;
  confirmCardPayment: () => Promise<void>;
  onSuccess: () => void;
  onPaymentButtonClick?: () => void;
};

const paymentElementOptions: StripePaymentElementOptions = {
  wallets: { applePay: 'never', googlePay: 'never' },
  layout: 'tabs',
  business: { name: 'Mindly Europe OÜ' },
};

const CardPaymentElement: React.FC<PaymentElementProps> = ({
  onReady,
  onSuccess,
  confirmCardPayment,
  onPaymentButtonClick,
}) => {
  const { t } = useTranslation('checkout');
  const { logError } = useLog();
  const { showToast } = useToast();

  const [paymentElementLoading, setPaymentElementLoading] = useState<boolean>(true);
  const [paymentElementConfirmButtonLoading, setPaymentElementConfirmButtonLoading] = useState<boolean>(false);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);

  const onPaymentElementReady = useCallback(
    (event: StripePaymentElement) => {
      setPaymentElementLoading(false);
      onReady?.(event);
    },
    [onReady]
  );

  const onPaymentElementConfirm = useCallback(
    async (event: React.MouseEvent<HTMLIonButtonElement>) => {
      try {
        onPaymentButtonClick?.();
        setPaymentElementConfirmButtonLoading(true);
        await confirmCardPayment();

        setPaymentSuccess(true);
        setPaymentElementConfirmButtonLoading(false);
        setTimeout(() => {
          onSuccess();
          setPaymentSuccess(false);
        }, 1000);
      } catch (error) {
        console.log('onPaymentElementConfirm error', error);
        logError(error);
        showToast({ error });
      } finally {
        setPaymentElementConfirmButtonLoading(false);
      }
    },
    [onSuccess, logError, showToast, confirmCardPayment, onPaymentButtonClick]
  );

  return (
    <>
      {paymentElementLoading && <Spinner className="mx-auto" />}
      <PaymentElement onReady={onPaymentElementReady} options={paymentElementOptions} />
      {!paymentElementLoading && (
        <Button
          className="w-full !mt-4 !text-white"
          onClick={onPaymentElementConfirm}
          size="large"
          isLoading={paymentElementConfirmButtonLoading}
          variant="blue"
        >
          {!paymentSuccess ? t('pay_now') : <IonIcon icon={checkmark} size={'large'} />}
        </Button>
      )}
    </>
  );
};

export default CardPaymentElement;
